/**
 * TrialRunCard (VH-280)
 *
 * One comparison run: today's model and the candidates on the same real cases, with
 * what decides a switch — the score (number features) or Reto's blind picks (free-text
 * features), cost per call against today's, response time and the monthly estimate.
 * A candidate above twice today's cost is flagged: the spec leaves that call to Reto.
 * So is one more than twice as slow as today's model and above 5 s (median): a quality
 * match that keeps Reto waiting is not switched quietly.
 */
import { useTranslation } from 'react-i18next'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { formatMs, formatRate, formatUsd } from './aiFormat'

function Result({ run, c, t, locale }) {
  if (run.kind === 'number') {
    if (c.match_rate == null) return <span className="text-gray-500">–</span>
    return (
      <span className="inline-flex items-center gap-1">
        {formatRate(c.match_rate, locale)}
        {c.passes === true && <CheckCircleIcon className="h-4 w-4 text-green-600" aria-label={t('admin.modelTrials.passes')} />}
        {c.passes === false && <XCircleIcon className="h-4 w-4 text-red-600" aria-label={t('admin.modelTrials.fails')} />}
      </span>
    )
  }
  if (c.is_today) return <span className="text-gray-500">{t('admin.modelTrials.reference')}</span>
  const p = c.picks ?? {}
  return (
    <span>{t('admin.modelTrials.picks', { better: p.candidate ?? 0, worse: p.today ?? 0, same: p.same ?? 0 })}</span>
  )
}

export default function TrialRunCard({ run, onReview }) {
  const { t, i18n } = useTranslation()
  const locale = i18n?.language || 'en'
  const date = (iso) => (iso ? new Date(iso).toLocaleDateString(locale) : '')

  return (
    <article className="rounded-lg border border-gray-200 p-4" data-testid={`trial-run-${run.id}`}>
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            {run.feature}
            <span className="ml-2 inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
              {t(`admin.modelTrials.kind.${run.kind}`)}
            </span>
          </h3>
          <p className="text-xs text-gray-500">
            {t('admin.modelTrials.meta', {
              cases: run.case_count, started: date(run.started_at), calls: run.calls_last_30_days,
            })}
            {' · '}
            {run.finished_at ? t('admin.modelTrials.finished', { date: date(run.finished_at) })
              : <span className="text-amber-700">{t('admin.modelTrials.unfinished')}</span>}
          </p>
          {run.notes && <p className="mt-1 text-xs text-amber-800">{run.notes}</p>}
        </div>
        {run.kind === 'text' && run.unreviewed > 0 && (
          <button type="button" className="btn-primary text-sm" onClick={() => onReview(run)}
            data-testid={`review-run-${run.id}`}>
            {t('admin.modelTrials.review', { count: run.unreviewed })}
          </button>
        )}
      </header>

      <div className="mt-3 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500">
              <th className="py-1 pr-3 font-medium">{t('admin.modelTrials.columns.model')}</th>
              <th className="py-1 pr-3 font-medium">
                {run.kind === 'number' ? t('admin.modelTrials.columns.score') : t('admin.modelTrials.columns.picks')}
              </th>
              <th className="py-1 pr-3 font-medium">{t('admin.modelTrials.columns.failed')}</th>
              <th className="py-1 pr-3 font-medium">{t('admin.modelTrials.columns.cost')}</th>
              <th className="py-1 pr-3 font-medium">{t('admin.modelTrials.columns.time')}</th>
              <th className="py-1 font-medium">{t('admin.modelTrials.columns.monthly')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {run.candidates.map((c) => (
              <tr key={c.candidate} data-testid={`trial-candidate-${c.candidate}`}>
                <td className="py-1.5 pr-3 whitespace-nowrap">
                  {c.candidate}
                  {c.is_today && <span className="ml-1 text-xs text-gray-500">({t('admin.modelTrials.today')})</span>}
                </td>
                <td className="py-1.5 pr-3 whitespace-nowrap"><Result run={run} c={c} t={t} locale={locale} /></td>
                <td className="py-1.5 pr-3">{c.failed}/{c.answered}</td>
                <td className="py-1.5 pr-3 whitespace-nowrap">
                  {formatUsd(c.avg_cost_usd, locale)}
                  {!c.is_today && c.cost_ratio != null && (
                    <span className={`ml-1 text-xs ${c.needs_cost_decision ? 'text-red-700 font-medium' : 'text-gray-500'}`}>
                      ×{c.cost_ratio}
                    </span>
                  )}
                </td>
                <td className="py-1.5 pr-3 whitespace-nowrap">
                  {formatMs(c.median_ms, locale)}
                  {!c.is_today && c.time_ratio != null && (
                    <span className={`ml-1 text-xs ${c.needs_time_decision ? 'text-red-700 font-medium' : 'text-gray-500'}`}>
                      ×{c.time_ratio}
                    </span>
                  )}
                  {c.needs_time_decision && (
                    <span className="block text-xs text-red-700" data-testid={`time-decision-${c.candidate}`}>
                      {t('admin.modelTrials.timeDecision')}
                    </span>
                  )}
                </td>
                <td className="py-1.5 whitespace-nowrap">
                  {formatUsd(c.monthly_estimate_usd, locale)}
                  {c.needs_cost_decision && (
                    <span className="block text-xs text-red-700" data-testid={`cost-decision-${c.candidate}`}>
                      {t('admin.modelTrials.costDecision')}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  )
}
